"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";
import QePixelPhoto from "../kit/QePixelPhoto";

/* Mobile hero carousel — Figma 12705:4967 "carrossel · hero (instância)" (390 × 314, feedback 28/10).
   Tiles 176.5 wide every 189 px, the active one centred at x 196.5; inactive tiles at 60 %. Host first, then the
   four traders. Each trader tile shows the CMS photo pixelated with the Figma crop, faded at the bottom and sides like
   the host. Over each tile one yellow tag (host: name, trader: the claim) and, on the main page, an arrow from under the
   tag to the face. Autoplay every 3.2 s, swipe to move, dots underneath (active = 20 × 4 yellow pill). */

export interface QeCarouselTrader { src?: string; tag?: string; subtag?: string }

// Figma crop of each trader photo inside the 176 × 210 tile (px), block = 34 image px at that scale, opacity.
const CROPS = [
  { left: -100.91, top: -23.47, w: 378.167, h: 346.413, block: 12.3, alpha: 0.71 }, // #1 Pro Scalper (px1)
  { left: -100.91, top: -23.47, w: 377.998, h: 363.733, block: 8.4, alpha: 0.75 }, // Trading World Champion (px4)
  { left: -174.83, top: -23.47, w: 526.114, h: 363.733, block: 11, alpha: 0.75 }, // Ex-Hedge Fund Manager (px2)
  { left: -80.96, top: -23.47, w: 338.111, h: 363.733, block: 8.1, alpha: 0.62 }, // Order Flow Expert (px3)
];

// Arrow width per trader (79 high, at 123.2 / 63 in the tile; it starts with the dot under the tag).
const ARROWS = [32.1677, 39.3026, 40.6873, 21.1893];

const STEP = 189;
const TILE = 176.5;
const CENTER = 196.5;
const r = (px: number) => `${px / 8}rem`;
const FADE = "absolute inset-0 [mask-composite:intersect] [mask-image:linear-gradient(180deg,#000_62%,transparent),linear-gradient(90deg,transparent,#000_14%,#000_86%,transparent)] [-webkit-mask-composite:source-in]";

/** Labels wider than the tile (> 18 characters) break at the middle word, so the tag hugs both lines (Figma 107 wide). */
function split(label: string) {
  const words = stegaClean(label).trim().split(/\s+/);
  if (words.join(" ").length <= 18 || words.length < 2) return words.join(" ");
  let at = 1;
  for (let k = 1, best = Infinity; k < words.length; k++) {
    const d = Math.abs(words.slice(0, k).join(" ").length - words.slice(k).join(" ").length);
    if (d < best) [best, at] = [d, k];
  }
  return `${words.slice(0, at).join(" ")}\n${words.slice(at).join(" ")}`;
}

/** Bottom edge at 64 px (main page, arrow underneath) or 84 px (thank-you, no arrow). */
function Tag({ label, low }: { label?: string; low?: boolean }) {
  if (!label) return null;
  return (
    <p className={cn("absolute left-1/2 mb-0 w-max -translate-x-1/2 -translate-y-full bg-[#fad65a] px-[1rem] py-[0.25rem] text-center font-geist text-[1.75rem] leading-[2.5rem] font-medium whitespace-pre-line text-black", low ? "top-[10.5rem]" : "top-[8rem]")}>
      {split(label)}
    </p>
  );
}

export default function QeHeroCarousel({ host, traders, alphas, arrows = true }: { host?: { src?: string; name?: string; role?: string }; traders: QeCarouselTrader[]; alphas?: number[]; arrows?: boolean }) {
  // The final section (12405:9655) runs the same carousel with the four traders only.
  const people = traders.slice(0, 4).map((t, i) => ({ kind: "trader" as const, i, ...t }));
  const items = host ? [{ kind: "host" as const, ...host }, ...people] : people;
  const n = items.length;
  // clones: last item before, first two after (Figma "t4a" / "chrisb" / "t1b")
  const track = [items[n - 1], ...items, items[0], items[1 % n]];
  const [idx, setIdx] = useState(1);
  const [anim, setAnim] = useState(true);
  const timer = useRef<number | null>(null);
  const reduce = useRef(false);

  const go = useCallback((next: number) => { setAnim(true); setIdx(next); }, []);

  useEffect(() => {
    reduce.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce.current || n < 2) return;
    timer.current = window.setInterval(() => setIdx((i) => { setAnim(true); return i + 1; }), 3200);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [n]);

  const onEnd = () => {
    if (idx > n) { setAnim(false); setIdx(idx - n); }
    else if (idx < 1) { setAnim(false); setIdx(idx + n); }
  };

  const start = useRef<number | null>(null);
  const onDown = (e: React.PointerEvent) => { start.current = e.clientX; };
  const onUp = (e: React.PointerEvent) => {
    if (start.current === null) return;
    const dx = e.clientX - start.current;
    start.current = null;
    if (Math.abs(dx) < 24) return;
    if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
    go(idx + (dx < 0 ? 1 : -1));
  };

  const active = ((idx - 1) % n + n) % n;
  const x = CENTER - (idx * STEP + TILE / 2);

  return (
    <div className="relative h-[39.25rem] w-full touch-pan-y overflow-hidden select-none lg:hidden" onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={() => (start.current = null)}>
      <div
        className={cn("absolute top-0 left-0 h-[36.25rem]", anim && "transition-transform duration-700 ease-[cubic-bezier(.65,0,.35,1)]")}
        style={{ width: r(track.length * STEP), transform: `translateX(${r(x)})` }}
        onTransitionEnd={onEnd}>
        {track.map((it, k) => {
          const on = k === idx;
          return (
            <div key={k} aria-hidden={!on || undefined} className={cn("absolute top-0 h-[36.25rem] w-[22.0625rem] transition-opacity duration-700", on ? "opacity-100" : "opacity-60")} style={{ left: r(k * STEP) }}>
              <div className={cn("absolute top-[9.5rem] left-0 h-[26.25rem] w-[22rem] overflow-hidden", it.kind === "trader" && "rounded-[0.2934rem]")}>
                {it.kind === "host" ? (
                  it.src && (
                    <div className={FADE}>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={`${stegaClean(it.src)}?w=700`} alt={on ? stegaClean(it.name || "") : ""} className="absolute top-[-2.884rem] left-[-9.4rem] h-[39.625rem] w-[39.125rem] max-w-none object-cover" />
                    </div>
                  )
                ) : (
                  <div className={FADE}>
                    <QePixelPhoto src={it.src} w={CROPS[it.i].w} h={CROPS[it.i].h} block={CROPS[it.i].block} style={{ left: r(CROPS[it.i].left), top: r(CROPS[it.i].top), opacity: alphas?.[it.i] ?? CROPS[it.i].alpha }} />
                  </div>
                )}
              </div>
              <Tag label={it.kind === "host" ? it.name : it.subtag || it.tag} low={!arrows} />
              {arrows && it.kind === "trader" && (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={`/quarterly-event/hero/arrow-m-${it.i + 1}.svg`} alt="" aria-hidden="true" className="absolute top-[7.875rem] left-[15.4rem] h-[9.875rem] max-w-none" style={{ width: r(ARROWS[it.i]) }} />
              )}
            </div>
          );
        })}
      </div>
      <div className="absolute top-[38.25rem] left-1/2 flex -translate-x-1/2 items-center gap-[0.75rem]" role="tablist" aria-label="Speakers">
        {items.map((_, k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={k === active}
            aria-label={`Show ${k + 1} of ${n}`}
            onClick={() => { if (timer.current) { window.clearInterval(timer.current); timer.current = null; } go(k + 1); }}
            className={cn("block h-[0.5rem] rounded-[0.25rem] transition-all duration-500", k === active ? "w-[2.5rem] bg-[#fad65a]" : "w-[0.5rem] bg-white/30")}
          />
        ))}
      </div>
    </div>
  );
}
